import { formatDistanceStrict, format } from 'date-fns'
import React, { useState, useEffect } from 'react'
import {
  formattedDateInTimeZone,
  IANATimezoneIdentifier,
  IETFLanguageTag,
  Timescale,
} from '@opallabs/concepts'
import { ZIndex } from '@opallabs/gem'
import { Input, renderIcon, kuiperClassNames } from '@opallabs/kuiper'
import { Tooltip, Icon, Button } from '@opallabs/lapidary'
import HeaderDurationDetails from './HeaderDurationDetails'
import * as styles from './styles'
import './styles.scss'

type ContentCardHeaderProps = {
  channelIcon: string
  color: string
  dayOffset: number
  disableInput: boolean
  isLocalized: boolean
  isPrivate?: boolean
  locale?: IETFLanguageTag
  openLocalization: () => void
  scheduledAt: string
  scheduledAtTimestamp: Date
  /** @deprecated Use `timeZoneIdentifier` */
  scheduledAtTimezone: string
  takedownScheduledAt: IANATimezoneIdentifier
  /** @deprecated Use `timeZoneIdentifier` */
  timezoneDifference?: number
  timeZoneIdentifier: string
  title: string
  updateTitle: (title: string) => void
  userTimezone: string
  wide?: boolean
}

const formatScheduledAt = ({
  locale,
  timestamp,
  timeZoneIdentifier,
}: {
  locale: IETFLanguageTag
  timestamp: Date
  timeZoneIdentifier: string
}) => {
  try {
    return formattedDateInTimeZone(timestamp, timeZoneIdentifier, {
      includeDate: false,
      includeZoneNameOrOffset: 'always',
      locale,
    })
  } catch {
    return null
  }
}

const ContentCardHeader: React.FC<ContentCardHeaderProps> = ({
  channelIcon,
  color,
  dayOffset,
  disableInput,
  isLocalized,
  isPrivate = false,
  locale = navigator.language,
  openLocalization,
  scheduledAt,
  scheduledAtTimestamp,
  scheduledAtTimezone,
  takedownScheduledAt,
  timezoneDifference = 0,
  timeZoneIdentifier,
  title,
  updateTitle,
  userTimezone,
  wide = false,
}) => {
  const [titleValue, setTitleValue] = useState(title)
  let duration

  if (takedownScheduledAt && scheduledAt !== takedownScheduledAt) {
    duration = formatDistanceStrict(
      new Date(scheduledAt),
      new Date(takedownScheduledAt),
      { unit: Timescale.Day }
    )
  }

  const formatOffset = (dayOffset: number) => {
    if (dayOffset === 0) return null
    if (dayOffset === 1) return '1 day'
    return `${dayOffset} days`
  }

  // This is necessary because we sometimes need
  // to update the title after the component has already
  // mounted with data that has changed in Backbone
  useEffect(() => {
    setTitleValue(title)
  }, [title])

  return (
    <div
      className={kuiperClassNames('content-card-header', {
        wide,
        private: isPrivate,
      })}
      style={{ backgroundColor: color }}
    >
      <div className={kuiperClassNames('post-title')}>
        {renderIcon(channelIcon, { ariaMode: 'hidden' })}
        <Input
          disabled={disableInput || isPrivate}
          maxLength={100}
          placeholder="Content title"
          onBlur={() => updateTitle(titleValue)}
          onChange={(value: string) => setTitleValue(value)}
          strict={true}
          ariaHidden={true}
          theme="invisible"
          value={titleValue}
        />
      </div>
      {!isPrivate && (
        <div className={kuiperClassNames('post-details')}>
          {dayOffset > 0 && (
            <span className={kuiperClassNames('scheduled-date')}>
              {format(scheduledAtTimestamp, 'MMM d')}
              {wide && (
                <span className={kuiperClassNames('offset-text')}>
                  (+{formatOffset(dayOffset)})
                </span>
              )}
            </span>
          )}
          <span className={kuiperClassNames('scheduled-time')}>
            {formatScheduledAt({
              locale,
              timestamp: scheduledAtTimestamp,
              timeZoneIdentifier,
            })}
          </span>
          {scheduledAt &&
            Boolean(duration || timezoneDifference || dayOffset > 0) && (
              <span
                className={kuiperClassNames('takedown', {
                  'has-duration': duration,
                })}
              >
                {wide && duration && (
                  <span className={kuiperClassNames('duration-text')}>
                    {Boolean(duration) && duration !== '0 days' && duration}
                  </span>
                )}
                <Tooltip
                  align="end"
                  zIndex={ZIndex.Shroud.Tooltip}
                  showArrow={true}
                  side="bottom"
                  legacyTheme="dark"
                  content={
                    <HeaderDurationDetails
                      dayOffset={dayOffset}
                      duration={duration}
                      scheduledAt={scheduledAt}
                      scheduledAtTimezone={scheduledAtTimezone}
                      takedownScheduledAt={takedownScheduledAt}
                      timezoneDifference={timezoneDifference}
                      userTimezone={userTimezone}
                    />
                  }
                >
                  <Button
                    className="content-card-duration"
                    css={styles.buttonOverrides}
                    name="content-duration-details"
                    theme="dark"
                    icon={<Icon glyph="gem-clock" />}
                    variant="ghost"
                  />
                </Tooltip>
              </span>
            )}
          {isLocalized && (
            <Tooltip
              content="This content is localized"
              zIndex={9999}
              legacyTheme="dark"
            >
              <Button
                className="content-card-localization"
                name="view-localization"
                theme="dark"
                css={styles.buttonOverrides}
                onClick={openLocalization}
                icon={<Icon glyph="localizations" useHypernova={true} />}
                variant="ghost"
              />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  )
}

export default ContentCardHeader
